import { z } from 'zod';

// Matches the backend's PasswordSchema in apps/lidz_back/src/models/User.ts:
// 14+ chars, at least one uppercase, lowercase, digit, and special character.
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_\-+=/[\]\\;'`~]).{14,}$/;

export const PASSWORD_ERROR_MESSAGE = 'Min. 14 caracteres con mayúscula, minúscula, número y carácter especial';

const baseShape = {
  name: z.string().trim().min(1, 'Requerido'),
  email: z.string().trim().toLowerCase().email('Email inválido'),
  phoneNumber: z.string().trim().optional().or(z.literal('')),
  externalId: z.string().trim().optional().or(z.literal('')),
  projectId: z.number().int().positive().nullable(),
  // Active projects of the Identity (multi-project).
  projectIds: z.array(z.number().int().positive()).default([]),
  subProjectIds: z.array(z.number().int().positive()).default([]),
  isAssignable: z.boolean().default(false),
  // Included in PATCH /full on Guardar (edit mode).
  isActive: z.boolean().default(true),
  roleIds: z.array(z.number().int().positive()).default([]),
};

export const createUserFormSchema = z
  .object({
    ...baseShape,
    password: z.string().regex(PASSWORD_REGEX, PASSWORD_ERROR_MESSAGE),
  })
  .refine((v) => v.projectId !== null, {
    message: 'Selecciona una inmobiliaria',
    path: ['projectId'],
  });

export const editUserFormSchema = z.object({
  ...baseShape,
  // Optional on edit: empty string means "do not change password".
  password: z
    .string()
    .refine((v) => v === '' || PASSWORD_REGEX.test(v), { message: PASSWORD_ERROR_MESSAGE })
    .default(''),
});

export const editUserAccountFormSchema = editUserFormSchema.pick({
  name: true,
  email: true,
  phoneNumber: true,
  password: true,
  projectIds: true,
});

export const editUserAccountAdminFormSchema = editUserFormSchema.pick({
  name: true,
  email: true,
  phoneNumber: true,
  password: true,
});

export const editUserProjectPermissionsFormSchema = editUserFormSchema.pick({
  isActive: true,
  externalId: true,
  subProjectIds: true,
  isAssignable: true,
  roleIds: true,
});

export const userFormSchemaFor = (mode: 'create' | 'edit'): typeof createUserFormSchema | typeof editUserFormSchema =>
  mode === 'create' ? createUserFormSchema : editUserFormSchema;

export type CreateUserFormValues = z.infer<typeof createUserFormSchema>;
export type EditUserFormValues = z.infer<typeof editUserFormSchema>;
export type UserFormValues = CreateUserFormValues | EditUserFormValues;
